/**
 * 钢笔工具绘制覆盖层
 *
 * 交互规则：
 *  - 单击 (mousedown → mouseup, 无位移)
 *      → 在 mousedown 位置添加直角锚点（直线段）
 *  - 按压拖拽 (mousedown → drag → mouseup)
 *      → mousedown 时在按压位置产生锚点
 *      → 拖拽期间实时显示贝塞尔曲线形态
 *      → mouseup 时固化控制手柄
 *  - 悬停起始点   → 起点高亮，单击闭合路径
 *  - 双击 / Enter / 工具条「结束绘制」→ 结束开放路径并写入画布
 *      · 第一次单击 mouseup 添加最后一个锚点
 *      · 第二次单击 mouseup（双击第二击）将按压位置暂存
 *      · dblclick 事件将暂存位置作为终点（若与上一锚点不同则追加），然后提交路径
 *  - Esc          → 放弃并退出（不写入画布）
 *
 * 退出时自动提交：覆盖层卸载时若已有 ≥2 个锚点则 commit（与涂抹笔一致；Esc 除外）
 *
 * 坐标系：
 *  - 交互全程使用画布像素坐标（SVG viewBox = 0 0 W H）
 *  - 提交时才将像素坐标归一化到 0–100，存入 VectorElement.pathData
 *
 * 线宽设计：
 *  - 预览描边固定 1px（使用 vectorEffect="non-scaling-stroke"）
 *  - 提交的路径 strokeWidth 默认 1（像素值，配合 vectorEffect="non-scaling-stroke"）
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import {
  toAbsoluteDrawCoords,
  type DrawOverlayInsertProps,
  type FrozenDrawInsertTarget,
} from '@/components/canvas/workspace/overlays/drawOverlayTypes';

// ─── types ─────────────────────────────────────────────────────────────────

/** 已固化的锚点（画布像素坐标 + 可选贝塞尔手柄，相对偏移） */
interface PenPoint {
  x: number;
  y: number;
  cpInX?: number;
  cpInY?: number;
  cpOutX?: number;
  cpOutY?: number;
}

/** 正在拖拽中的临时状态 */
interface DragState {
  /** 按压位置（新锚点所在位置） */
  anchorX: number;
  anchorY: number;
  /** 当前鼠标位置（cpOut 手柄末端） */
  currentX: number;
  currentY: number;
}

export interface PenToolOverlayProps extends DrawOverlayInsertProps {
  pageWidth: number;
  pageHeight: number;
  /** 当前画布缩放（用于让把手视觉大小保持恒定） */
  zoom: number;
}

const SNAP_PX = 12; // 吸附起始点的阈值（画布像素）

// ─── path builders ─────────────────────────────────────────────────────────

/** 在画布像素空间生成 SVG path d 字符串（用于实时预览） */
function buildPixelPathD(
  points: PenPoint[],
  closed: boolean,
  ghostEnd?: { x: number; y: number },
): string {
  const pts: PenPoint[] = ghostEnd
    ? [...points, { x: ghostEnd.x, y: ghostEnd.y }]
    : [...points];

  if (pts.length === 0) return '';

  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;

  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const hasOut = prev.cpOutX !== undefined;
    const hasIn  = curr.cpInX  !== undefined;

    if (hasOut || hasIn) {
      const c1x = prev.x + (prev.cpOutX ?? 0);
      const c1y = prev.y + (prev.cpOutY ?? 0);
      const c2x = curr.x + (curr.cpInX  ?? 0);
      const c2y = curr.y + (curr.cpInY  ?? 0);
      d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)},${c2x.toFixed(1)} ${c2y.toFixed(1)},${curr.x.toFixed(1)} ${curr.y.toFixed(1)}`;
    } else {
      d += ` L ${curr.x.toFixed(1)} ${curr.y.toFixed(1)}`;
    }
  }

  if (closed) d += ' Z';
  return d;
}

/** 归一化坐标，用于提交到 Store */
function buildNormalizedPathD(
  points: PenPoint[],
  closed: boolean,
  ox: number, oy: number,
  w: number,  h: number,
): string {
  if (points.length === 0) return '';
  const nx = (v: number) => (w < 1 ? 50 : ((v - ox) / w) * 100);
  const ny = (v: number) => (h < 1 ? 50 : ((v - oy) / h) * 100);

  let d = `M ${nx(points[0].x).toFixed(3)} ${ny(points[0].y).toFixed(3)}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const hasOut = prev.cpOutX !== undefined;
    const hasIn  = curr.cpInX  !== undefined;

    if (hasOut || hasIn) {
      d += ` C ${nx(prev.x + (prev.cpOutX ?? 0)).toFixed(3)} ${ny(prev.y + (prev.cpOutY ?? 0)).toFixed(3)},`;
      d +=   `${nx(curr.x + (curr.cpInX  ?? 0)).toFixed(3)} ${ny(curr.y + (curr.cpInY  ?? 0)).toFixed(3)},`;
      d +=   `${nx(curr.x).toFixed(3)} ${ny(curr.y).toFixed(3)}`;
    } else {
      d += ` L ${nx(curr.x).toFixed(3)} ${ny(curr.y).toFixed(3)}`;
    }
  }
  if (closed) d += ' Z';
  return d;
}

// ─── component ────────────────────────────────────────────────────────────

export function PenToolOverlay({
  pageWidth,
  pageHeight,
  zoom,
  insertVia,
  pageId,
  originX = 0,
  originY = 0,
  activatePageOnDraw = false,
  onSessionEnd,
}: PenToolOverlayProps) {
  const { addVectorElement, setPenToolActive, setCurrentPageId } = useCanvas();
  const svgRef = useRef<SVGSVGElement>(null);

  /** 已固化的锚点列表（React 状态，驱动渲染） */
  const [committed, setCommitted] = useState<PenPoint[]>([]);
  /** 当前正在拖拽中的状态（null = 未按压） */
  const [drag, setDrag] = useState<DragState | null>(null);
  /** 鼠标悬停位置（用于虚线预览） */
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  /** 鼠标是否在起始点附近（吸附闭合） */
  const [nearStart, setNearStart] = useState(false);

  /**
   * 命令式 ref：始终保存最新的已固化锚点列表。
   * 与 React 状态不同，这个 ref 在 mouseup 事件处理中同步更新，
   * 确保 dblclick 等后续事件读取到的是真正最新的数据，
   * 而不是可能因 React 批量渲染还未完成而产生的旧快照。
   */
  const latestCommittedRef = useRef<PenPoint[]>([]);

  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;
  const nearStartRef = useRef(false);
  nearStartRef.current = nearStart;

  /** 上次 mouseup 的时间戳，用于双击检测 */
  const lastUpTimeRef = useRef(0);
  /** Esc 放弃路径时置 true，卸载覆盖层不自动提交 */
  const abandonOnUnmountRef = useRef(false);
  const insertTargetRef = useRef<FrozenDrawInsertTarget | null>(null);

  const freezeInsertTarget = useCallback(() => {
    if (!insertTargetRef.current) {
      insertTargetRef.current = {
        via: insertVia,
        pageId,
        originX,
        originY,
      };
    }
    return insertTargetRef.current;
  }, [insertVia, pageId, originX, originY]);

  // ── canvas-pixel coord from mouse event ─────────────────────────────────

  const getPos = useCallback(
    (e: React.MouseEvent | MouseEvent): { x: number; y: number } => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return {
        x: (e.clientX - rect.left) / zoom,
        y: (e.clientY - rect.top)  / zoom,
      };
    },
    [zoom],
  );

  // ── commit path to store ─────────────────────────────────────────────────

  const commit = useCallback(
    (pts: PenPoint[], closed: boolean) => {
      if (pts.length < 2) {
        latestCommittedRef.current = [];
        setCommitted([]);
        setPenToolActive(false);
        return;
      }

      const xs = pts.map((p) => p.x);
      const ys = pts.map((p) => p.y);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      const bw   = Math.max(Math.max(...xs) - minX, 10);
      const bh   = Math.max(Math.max(...ys) - minY, 10);

      const pathData = buildNormalizedPathD(pts, closed, minX, minY, bw, bh);
      const target = freezeInsertTarget();
      const abs = toAbsoluteDrawCoords(minX, minY, target.originX, target.originY);
      addVectorElement('path', {
        x: abs.x,
        y: abs.y,
        width: bw,
        height: bh,
        via: target.via,
        pageId: target.pageId,
        data: { shape: 'path', pathData },
        appearance: {
          fills: closed ? [{ type: 'solid', color: '#e5e5e5' }] : undefined,
          strokes: [{ color: '#333', width: 1, style: 'solid' }],
        },
      });
      latestCommittedRef.current = [];
      setCommitted([]);
      insertTargetRef.current = null;
      setPenToolActive(false);
      onSessionEnd?.();
    },
    [addVectorElement, setPenToolActive, freezeInsertTarget, onSessionEnd],
  );

  const commitRef = useRef(commit);
  commitRef.current = commit;

  // ── 退出时自动提交（工具条「结束绘制」等；Esc 不提交）──────────────────

  useEffect(() => {
    return () => {
      if (abandonOnUnmountRef.current) {
        latestCommittedRef.current = [];
        return;
      }

      let pts = [...latestCommittedRef.current];
      const ds = dragRef.current;
      if (ds) {
        const ddx = ds.currentX - ds.anchorX;
        const ddy = ds.currentY - ds.anchorY;
        const isDrag = Math.abs(ddx) > 3 || Math.abs(ddy) > 3;
        const newPt: PenPoint = isDrag
          ? {
              x: ds.anchorX,
              y: ds.anchorY,
              cpOutX: ddx,
              cpOutY: ddy,
              cpInX: -ddx,
              cpInY: -ddy,
            }
          : { x: ds.anchorX, y: ds.anchorY };
        if (pts.length > 0) {
          const last = pts[pts.length - 1];
          const dist = Math.sqrt((newPt.x - last.x) ** 2 + (newPt.y - last.y) ** 2);
          if (dist >= 3) pts = [...pts, newPt];
        } else {
          pts = [newPt];
        }
      }

      if (pts.length >= 2) {
        commitRef.current(pts, false);
      } else {
        latestCommittedRef.current = [];
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── keyboard shortcuts ───────────────────────────────────────────────────

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        abandonOnUnmountRef.current = true;
        latestCommittedRef.current = [];
        setCommitted([]);
        setPenToolActive(false);
      } else if (e.key === 'Enter') {
        commit(latestCommittedRef.current, false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [commit, setPenToolActive]);

  // ── mouse handlers ───────────────────────────────────────────────────────

  /**
   * mousedown：在按压位置产生新锚点，进入拖拽模式。
   * 控制手柄尚未固化，等待 mousemove 更新。
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      freezeInsertTarget();
      if (activatePageOnDraw && pageId) {
        setCurrentPageId(pageId);
      }

      const pos = getPos(e);
      setDrag({ anchorX: pos.x, anchorY: pos.y, currentX: pos.x, currentY: pos.y });
    },
    [getPos, freezeInsertTarget, activatePageOnDraw, pageId, setCurrentPageId],
  );

  /**
   * mousemove：
   *  - 有拖拽：实时更新 cpOut 手柄末端位置
   *  - 无拖拽：更新悬停预览位置 + 检测是否靠近起点
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const pos = getPos(e);
      setHover(pos);

      if (dragRef.current) {
        setDrag((prev) =>
          prev ? { ...prev, currentX: pos.x, currentY: pos.y } : null,
        );
      }

      const pts = latestCommittedRef.current;
      if (pts.length > 0) {
        const first = pts[0];
        const dx = pos.x - first.x;
        const dy = pos.y - first.y;
        setNearStart(Math.sqrt(dx * dx + dy * dy) < SNAP_PX);
      } else {
        setNearStart(false);
      }
    },
    [getPos],
  );

  /**
   * mouseup：固化锚点。
   *
   * 双击检测：若距上次 mouseup < 300ms 且已有锚点，则判定为双击第二击，
   * 直接跳过（不添加多余节点），由 dblclick 事件负责提交。
   *
   * 关键：latestCommittedRef 在第一击 mouseup 中同步更新（非 React 异步批量），
   * 确保 dblclick 事件读取到包含正确终点的最新数据，不受渲染时机影响。
   */
  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      const ds = dragRef.current;
      setDrag(null);
      if (!ds) return;

      const now = Date.now();

      // ① 双击第二击：跳过，dblclick 负责提交
      if (now - lastUpTimeRef.current < 300 && latestCommittedRef.current.length > 0) {
        return;
      }
      lastUpTimeRef.current = now;

      const ddx = ds.currentX - ds.anchorX;
      const ddy = ds.currentY - ds.anchorY;
      const isDrag = Math.abs(ddx) > 3 || Math.abs(ddy) > 3;

      const newPt: PenPoint = isDrag
        ? {
            x: ds.anchorX, y: ds.anchorY,
            cpOutX:  ddx, cpOutY:  ddy,
            cpInX:  -ddx, cpInY: -ddy,
          }
        : { x: ds.anchorX, y: ds.anchorY };

      const existing = latestCommittedRef.current;

      // ② 重合点：跳过，防止在已有锚点上双击产生弧形折返线。
      // 场景：用户在 B 点放锚后想"双击 B 结束"——此时第一击落于 B，
      // 若不过滤会多添加一个与 B 重合的节点，B 的 cpOut 手柄会在 B→B
      // 段生成可见的弧形。
      if (existing.length > 0) {
        const last = existing[existing.length - 1];
        const dist = Math.sqrt((newPt.x - last.x) ** 2 + (newPt.y - last.y) ** 2);
        if (dist < 3) return;
      }

      // ③ 吸附闭合
      if (nearStartRef.current && existing.length >= 2) {
        commit(existing, true);
        return;
      }

      // 同步更新命令式 ref，再更新 React 状态
      latestCommittedRef.current = [...existing, newPt];
      setCommitted(latestCommittedRef.current);
    },
    [commit],
  );

  /**
   * dblclick：提交路径。
   *
   * 第一击 mouseup 已将终点加入 latestCommittedRef（通过同步更新的命令式 ref）；
   * 第二击 mouseup 被时间检测跳过，不添加多余节点。
   * 此处直接提交当前路径，无需额外处理。
   */
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      commit(latestCommittedRef.current, false);
    },
    [commit],
  );

  // ── derive preview path ──────────────────────────────────────────────────

  /**
   * 拖拽进行中时，将当前拖拽状态合成一个临时锚点插入路径末尾，
   * 让用户实时看到曲线形态。
   */
  const isDragging =
    drag !== null &&
    (Math.abs(drag.currentX - drag.anchorX) > 1 ||
     Math.abs(drag.currentY - drag.anchorY) > 1);

  let previewD = '';

  if (isDragging && drag) {
    const cpOutX = drag.currentX - drag.anchorX;
    const cpOutY = drag.currentY - drag.anchorY;
    const tempPt: PenPoint = {
      x: drag.anchorX, y: drag.anchorY,
      cpOutX, cpOutY,
      cpInX: -cpOutX, cpInY: -cpOutY,
    };
    previewD = buildPixelPathD([...committed, tempPt], false);
  } else if (committed.length > 0 && hover) {
    // 当鼠标回到最后一个锚点附近（< 3px）时，不显示 ghost 预览线：
    // 没有距离就没有角度，显示一条长度为零的线没有意义，
    // 且这正是用户想"双击此处结束"的信号。
    const lastPt = committed[committed.length - 1];
    const hoverDist = Math.sqrt((hover.x - lastPt.x) ** 2 + (hover.y - lastPt.y) ** 2);
    if (hoverDist > 3) {
      previewD = buildPixelPathD(committed, false, hover);
    } else {
      previewD = buildPixelPathD(committed, false);
    }
  } else if (committed.length > 0) {
    previewD = buildPixelPathD(committed, false);
  }

  // 首个节点尚未 committed 但已按压时，显示按压点位置
  const pendingAnchor: { x: number; y: number } | null =
    drag && committed.length === 0 ? { x: drag.anchorX, y: drag.anchorY } : null;

  const firstPt = committed[0] ?? null;

  // 把手视觉尺寸（屏幕像素恒定，除以 zoom 转换到画布像素空间）
  const hr    = 4   / zoom; // 普通把手圆半径
  const sr    = 7   / zoom; // 吸附起点圆半径
  const sw    = 1   / zoom; // 路径描边宽（屏幕1px）
  const hsw   = 1   / zoom; // 把手线描边宽
  const fsize = 12  / zoom;
  const dash  = `${5 / zoom} ${3 / zoom}`;

  return (
    <svg
     
      ref={svgRef}
      viewBox={`0 0 ${pageWidth} ${pageHeight}`}
      width={pageWidth}
      height={pageHeight}
      style={{
        position: 'absolute',
        inset: 0,
        cursor: nearStart ? 'cell' : 'crosshair',
        userSelect: 'none',
        zIndex: 1000,
        overflow: 'visible',
        pointerEvents: 'auto',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onDoubleClick={handleDoubleClick}
    >
      {/* ── 主预览路径 ── */}
      {previewD && (
        <path
          d={previewD}
          fill="none"
          stroke="#2563eb"
          strokeWidth={sw}
          strokeDasharray={dash}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* ── 闭合预览高亮 ── */}
      {nearStart && committed.length >= 2 && (
        <path
          d={buildPixelPathD(committed, true)}
          fill="rgba(37,99,235,0.08)"
          stroke="#2563eb"
          strokeWidth={sw}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* ── 拖拽中的贝塞尔手柄 ── */}
      {isDragging && drag && (() => {
        const cpOutX = drag.currentX - drag.anchorX;
        const cpOutY = drag.currentY - drag.anchorY;
        return (
          <g>
            <line
              x1={drag.anchorX} y1={drag.anchorY}
              x2={drag.anchorX + cpOutX} y2={drag.anchorY + cpOutY}
              stroke="#2563eb" strokeWidth={hsw}
            />
            <circle cx={drag.anchorX + cpOutX} cy={drag.anchorY + cpOutY}
              r={hr} fill="#fff" stroke="#2563eb" strokeWidth={hsw} />
            <line
              x1={drag.anchorX} y1={drag.anchorY}
              x2={drag.anchorX - cpOutX} y2={drag.anchorY - cpOutY}
              stroke="#2563eb" strokeWidth={hsw}
            />
            <circle cx={drag.anchorX - cpOutX} cy={drag.anchorY - cpOutY}
              r={hr} fill="#fff" stroke="#2563eb" strokeWidth={hsw} />
            <circle cx={drag.anchorX} cy={drag.anchorY}
              r={hr} fill="#2563eb" stroke="#fff" strokeWidth={hsw} />
          </g>
        );
      })()}

      {/* ── 已固化的锚点及其手柄 ── */}
      {committed.map((pt, i) => (
        <g key={i}>
          {pt.cpOutX !== undefined && (
            <>
              <line x1={pt.x} y1={pt.y}
                x2={pt.x + pt.cpOutX} y2={pt.y + (pt.cpOutY ?? 0)}
                stroke="#2563eb" strokeWidth={hsw} strokeDasharray={dash} />
              <circle cx={pt.x + pt.cpOutX} cy={pt.y + (pt.cpOutY ?? 0)}
                r={hr} fill="#fff" stroke="#2563eb" strokeWidth={hsw} />
            </>
          )}
          {pt.cpInX !== undefined && (
            <>
              <line x1={pt.x} y1={pt.y}
                x2={pt.x + pt.cpInX} y2={pt.y + (pt.cpInY ?? 0)}
                stroke="#2563eb" strokeWidth={hsw} strokeDasharray={dash} />
              <circle cx={pt.x + pt.cpInX} cy={pt.y + (pt.cpInY ?? 0)}
                r={hr} fill="#fff" stroke="#2563eb" strokeWidth={hsw} />
            </>
          )}
          <circle
            cx={pt.x} cy={pt.y}
            r={i === 0 && nearStart ? sr : hr}
            fill={i === 0 && nearStart ? '#2563eb' : '#fff'}
            stroke="#2563eb"
            strokeWidth={sw}
          />
        </g>
      ))}

      {/* ── 按压但未释放时的悬浮锚点（第一个节点） ── */}
      {pendingAnchor && (
        <circle cx={pendingAnchor.x} cy={pendingAnchor.y}
          r={hr} fill="#2563eb" stroke="#fff" strokeWidth={hsw} />
      )}

      {/* ── 吸附闭合提示 ── */}
      {nearStart && firstPt && (
        <text
          x={firstPt.x + sr + 2 / zoom}
          y={firstPt.y - sr}
          fontSize={fsize}
          fill="#2563eb"
          style={{ pointerEvents: 'none', fontFamily: 'system-ui, sans-serif' }}
        >
          闭合
        </text>
      )}
    </svg>
  );
}
